import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CancelBatchList.css';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const CancelBatchList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [batches, setBatches] = useState([]);
  const { selectedCenter, setSelectedCenter } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('auth-token');



  const fetchCancelledBatches = async () => {
    if (!selectedCenter) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ACADEMY_COACHES}/${selectedCenter}/cancelled`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const result = await response.json();

      if (result.success && result.data?.canceledBatchesList) {
        const formattedBatches = result.data.canceledBatchesList.map(
          (batch) => ({
            id: batch.id,
            batchName: batch.batch.name,
            cancelledOn: batch.canceledOn.split("T")[0],
            cancellationReason: batch.cancellationReason,
          })
        );
        setBatches(formattedBatches);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCancelledBatches();
  }, [selectedCenter]);

  const headers = [
    { label: "Batch Name", key: "batchName" },
    { label: "Cancelled On", key: "cancelledOn" },
    { label: "Cancellation Reason", key: "cancellationReason" },
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [action, setAction] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const navigate = useNavigate();


  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };


  const handleBatchSelect = (batchId) => {
    const updatedSelection = selectedBatches.includes(batchId)
      ? selectedBatches.filter(id => id !== batchId)
      : [...selectedBatches, batchId];
    setSelectedBatches(updatedSelection);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBatches([]);
    } else {
      setSelectedBatches(batches.map(batch => batch.id));
    }
    setSelectAll(!selectAll);
  };


  const filteredBatches = batches.filter(batch =>
    batch.batchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    batch.cancelledOn.toLowerCase().includes(searchTerm.toLowerCase()) ||
    batch.cancellationReason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  const handleSort = (columnKey) => {
    let direction = "ascending";
    if (sortConfig.key === columnKey && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === "ascending" ? -1 : 1;
      if (a[key] > b[key]) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };
  const sortedData = sortData(filteredBatches);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);


  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prevPage => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prevPage => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const handleUncancel = async () => {
    if (selectedBatches.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No batch selected!",
        text: "Please select at least one batch to uncancel.",
      });
      return;
    }

    const batchIdsQuery = selectedBatches.join(",");
    const apiUrl = `${process.env.REACT_APP_API_ACADEMY_COACHES}/${selectedCenter}/delete-cancellation?canceledBatchId=${batchIdsQuery}`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`,
        },
      });

      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error("Invalid server response. Please try again.");
      }

      if (!response.ok || result.success === false) {
        throw new Error(result.data?.errorMessage || "Failed to uncancel batches.");
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Batches successfully uncanceled!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      setBatches((prevBatches) =>
        prevBatches.filter((batch) => !selectedBatches.includes(batch.id))
      );
      setSelectedBatches([]);

    } catch (error) {

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Something went wrong! Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

    }
  };

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const totalEntries = filteredBatches.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  return (
    <div className="enrollment-container">
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button className="create-button" onClick={handleUncancel}>
          Uncancel
        </button>
      </div>

      <div className="items-and-dues">
      <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
        <option value={10}>10 items per page</option>
        <option value={50}>50 items per page</option>
        <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      <button className="tt-action-btn" onClick={fetchCancelledBatches}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
        </div>
      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredBatches.length === 0 && (
        <div className="error-message">No data available</div>
      )}
      
      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </th>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((batch) => (
              <tr key={batch.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedBatches.includes(batch.id)}
                    onChange={() => handleBatchSelect(batch.id)}
                  />
                </td>
                <td>{batch.batchName}</td>
                <td>{batch.cancelledOn}</td>
                <td>{batch.cancellationReason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CancelBatchList;
